import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getTrainingSettings, updateTrainingSettings } from './api';

// Where clients land when they click through to take a class. Lives in the
// database, not an env var, so the Director can repoint it without a deploy.
export default function ClientPortalSettings() {
  const qc = useQueryClient();
  const { data, isLoading, isError } = useQuery({ queryKey: ['training-settings'], queryFn: getTrainingSettings, retry: false });
  const [form, setForm] = useState({ lms_url: '', lms_note: '' });
  // Seed from the server ONCE. The app refetches on window focus, so re-seeding on
  // every `data` change silently wiped a URL the Director was midway through
  // pasting after they alt-tabbed to copy it.
  const seeded = useRef(false);

  useEffect(() => {
    if (seeded.current || !data?.settings) return;
    seeded.current = true;
    setForm({ lms_url: data.settings.lms_url || '', lms_note: data.settings.lms_note || '' });
  }, [data]);

  const save = useMutation({
    mutationFn: () => updateTrainingSettings({ lms_url: form.lms_url.trim(), lms_note: form.lms_note.trim() }),
    onSuccess: () => {
      // Show what was actually stored. We send trimmed values and seed the form
      // only once, so without this the inputs keep displaying the user's stray
      // whitespace while the database holds the trimmed version.
      setForm((f) => ({ lms_url: f.lms_url.trim(), lms_note: f.lms_note.trim() }));
      toast.success('Client portal settings saved');
      qc.invalidateQueries({ queryKey: ['training-settings'] });
      qc.invalidateQueries({ queryKey: ['training-me'] });
    },
    onError: (e) => toast.error(e?.message || 'Could not save'),
  });

  if (isLoading) return <div className="text-xs text-gray-500">Loading...</div>;
  if (isError) return <div className="text-xs text-red-500">Could not load client portal settings.</div>;

  const field = 'w-full text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 mt-1';

  return (
    <div className="max-w-[620px]">
      <h3 className="text-sm font-semibold mb-1">Client portal</h3>
      <p className="text-xs text-gray-500 mb-4">
        Clients see a "Take your training" panel on My Classes that points here. A class can override this with its own
        link under Catalog; anything left blank falls back to this URL.
      </p>

      <label className="block text-xs text-gray-500">
        LMS URL
        <input
          type="url"
          value={form.lms_url}
          onChange={(e) => setForm({ ...form, lms_url: e.target.value })}
          placeholder="https://learn.zeffert.com"
          className={field}
        />
      </label>
      <p className="text-[11px] text-gray-400 mt-1">
        Leave blank to hide the LMS panel and every class-level LMS button.
      </p>

      <label className="block text-xs text-gray-500 mt-4">
        Note shown beside the link
        <textarea
          rows={2}
          value={form.lms_note}
          onChange={(e) => setForm({ ...form, lms_note: e.target.value })}
          placeholder="We set up your LMS account within one business day of purchase."
          className={field}
        />
      </label>

      <button
        onClick={() => save.mutate()}
        disabled={save.isPending}
        className="mt-4 text-sm font-bold px-4 py-2 rounded-lg bg-blue-600 text-white disabled:opacity-40"
      >
        {save.isPending ? 'Saving...' : 'Save'}
      </button>
    </div>
  );
}
